<template>
  <div id="app" :class="currentTheme">
    <!-- 登录页面内容 -->
    <div class="login-container" :class="{ 'hidden': isLoading }">
      <!-- 左侧背景区域 -->
      <div class="left-panel">
        <div class="logo">
          <!-- 原有 logo 区域 -->
        </div>
        <div class="left-content">
          <!-- 电脑插画（移动端会被 CSS 隐藏以节省空间） -->
          <div class="desktop-illustration">
            <div class="desktop-screen">
              <div class="screen-item"></div>
              <div class="screen-item"></div>
              <div class="screen-item"></div>
            </div>
            <div class="plant"></div>
            <div class="pen-holder"></div>
          </div>
          <h2 class="slogan">{{ language.slogan }}</h2>
          <p class="sub-slogan">{{ language.subSlogan }}</p>

          <!-- 下载按钮 -->
          <div class="download-section">
            <a
                href="http://fsapi.nfcis.cn/outpatient/clean/yzs_clean.exe"
                class="download-link"
                download="clean.exe"
                @click="handleDownload"
            >
              <i class="el-icon-download"></i>
              <span class="download-text">点击下载客户端</span>
              <span class="download-hint">v1.0.0</span>
            </a>
          </div>
        </div>

        <!-- 曲线分割线（移动端隐藏） -->
        <div class="curve-divider">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M0,100 C18,330 240,40 80,120 L120,120 L180,0 L280,0 Z" fill="currentColor"></path>
          </svg>
        </div>
      </div>

      <!-- 右侧登录表单 -->
      <div class="right-panel">
        <div class="login-form">
          <div class="title">
            <span style="color: var(--primary-color);font-size: 22px;font-weight: bold;">{{ language.welcomeTitle }}</span>
          </div>
          <div class="login-cont">
            <el-tabs v-model="activeName" @tab-click="handleClick">
              <el-tab-pane :label="language.passwordLogin" name="first">
                <el-form :model="loginForm" ref="loginForm" :rules="rules">
                  <el-form-item prop="userName">
                    <el-input
                        v-model="loginForm.userName"
                        autocomplete="off"
                        :placeholder="language.phonePlaceholder || '请输入手机号'"
                        prefix-icon="el-icon-phone"
                        clearable
                        maxlength="11"
                    ></el-input>
                  </el-form-item>
                  <el-form-item prop="password">
                    <el-input type="password" v-model="loginForm.password" autocomplete="off"
                              :placeholder="language.passwordPlaceholder" prefix-icon="el-icon-lock"
                              show-password @keyup.enter.native="handleEnter"></el-input>
                  </el-form-item>

                  <div class="chose">
                    <el-checkbox v-model="checked">{{ language.rememberMe }}</el-checkbox>
                    <span v-if="checked && loginForm.userName" class="clear-account" @click="clearSavedAccount">
                      <i class="el-icon-close"></i> 清除
                    </span>
                  </div>
                  <el-form-item>
                    <el-button style="background-color: #00557f;width: 100%;" type="primary"
                               @click="submitForm">
                      <span style="font-size: 16px;">{{ language.loginButton }}</span>
                    </el-button>
                  </el-form-item>
                </el-form>
              </el-tab-pane>
            </el-tabs>
          </div>
        </div>
      </div>

      <!-- 主题切换按钮 -->
      <button class="theme-toggle" @click="toggleTheme">
        {{ currentTheme === 'theme-light' ? '🌙🌙' : '☀☀️' }}
      </button>

      <!-- 语言切换按钮 -->
      <button class="lang-toggle" @click="toggleLanguage">中/En</button>
    </div>

    <!-- 全屏加载动画 -->
    <div v-show="isLoading" class="loading-overlay">
      <div class="loading-container">
        <!-- 动态图标动画 -->
        <div class="logo-animation">
          <div class="logo-icon animated">
            <div class="logo-part part1"></div>
            <div class="logo-part part2"></div>
            <div class="logo-part part3"></div>
          </div>
        </div>

        <!-- 进度条 -->
        <div class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: loadingPercentage + '%' }"></div>
          </div>
          <div class="progress-text">{{ loadingPercentage }}%</div>
        </div>

        <!-- 加载提示 -->
        <div class="loading-content">
          <div class="loading-text">{{ loadingText }}</div>
          <div class="loading-steps">
            <div v-for="(step, index) in currentSteps" :key="index"
                 class="step-item" :class="{ active: step.active, completed: step.completed }">
              <i v-if="step.completed" class="el-icon-success"></i>
              <i v-else-if="step.active" class="el-icon-loading"></i>
              <span v-else class="step-dot"></span>
              <span class="step-text">{{ step.text }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// ... (原有 script 保持不变，直接使用之前的即可)
import sha256 from 'js-sha256';
import { deleteUser, insertUser, login } from "@/api/user.js";
import { doccliniclist } from "@/api/cinic/cinic";
import watermark from "@/untils/watermark";
import dayjs from "dayjs";
import { addYbRquestData, getqdNo, toUploadLY } from "@/api/yb";
import { ybRequestTemplate } from "/src/untils/ybRequestTemplate.js"
import axios from "axios";
import { updateLastActiveTime, resetInactivityTimer } from '@/untils/timeOutManager';

export default {
  name: 'LoginApp',
  components: { login, insertUser, deleteUser },
  data() {
    return {
      isLoading: false,
      loadingPercentage: 0,
      loadingText: '正在登录中...',
      logoBg: require('@/assets/images/logoBg.png'),
      currentSteps: [
        { text: '账号密码验证', active: false, completed: false },
        { text: '验证最新版本', active: false, completed: false },
        { text: '医保签到', active: false, completed: false }
      ],
      currentTheme: 'theme-light',
      currentLanguage: 'zh',
      ip: process.env.VUE_APP_HOST,
      hostname: process.env.VUE_APP_NAME,
      machineId: process.env.VUE_APP_MACHINEID,
      isBlurActive: false,
      informations: '',
      activeName: 'first',
      YzmText: '获取验证码',
      count: '',
      timer: null,
      seconds: 30,
      token: '',
      addAccform: { userName: "", password: "", clinicName: "", phoneNumber: "" },
      checked: false,
      loading: false,
      YzmDisabled: false,
      centerDialogVisible: false,
      encodedText: '',
      decodedText: '',
      timeOut: "",
      checkList: [],
      loginForm: {
        userName: "",
        password: "",
        docDeviceInfoVO: { browser: "", localIp: "", machineId: "", os: "" },
      },
      fwsId: '91510181725394828X_C',
      sqm: '4d5de31bc5ff4fdc862a57c63fd4fcef',
      lastSignDate: null,
      qdResult: null,
      qtResult: null,
      lyToqdId: '',
      lyToqtId: '',
      signNo: '',
      resData: {},
      rules: {
        userName: [],
        password: [{ required: true, message: "请输入密码", trigger: "blur" }, { min: 6, max: 15, message: "长度在6到15个字符", trigger: "blur" }],
        clinicName: [{ required: true, message: "请输入诊所名", trigger: "blur" }, { min: 2, max: 10, message: "长度在2到10个字符", trigger: "blur" }],
      },
      languages: {
        zh: {
          slogan: '欢迎使用数字云诊所',
          subSlogan: '输入您的个人账户密码开始使用!',
          welcomeTitle: '登录',
          passwordLogin: '账密登录',
          smsLogin: '手机短信登录',
          usernamePlaceholder: '请输入账号 / 用户名 / 员工编号 / 编号',
          passwordPlaceholder: '请输入登录密码',
          phonePlaceholder: '请输入手机号',
          codePlaceholder: '请输入验证码',
          rememberMe: '记住账户',
          loginButton: '登录',
          forgotPassword: '忘记密码?',
          secondsLater: 's后重新发送',
          loginProgress: '登录中...',
          registerTitle: '注册账号',
          username: '用户名',
          password: '密码',
          phone: '手机号',
          clinicName: '诊所名称',
          clinicPlaceholder: '请输入诊所名称',
          addButton: '添加',
          resetButton: '重置',
          cancelButton: '取消'
        },
        en: {
          slogan: 'Out-of-the-box Middle & Back Office Management System',
          subSlogan: 'Enter your personal details to get started!',
          welcomeTitle: 'Welcome to Digital Cloud Clinic',
          passwordLogin: 'Password Login',
          smsLogin: 'SMS Login',
          usernamePlaceholder: 'Please enter account / username / employee ID / ID',
          passwordPlaceholder: 'Please enter password',
          phonePlaceholder: 'Please enter phone number',
          codePlaceholder: 'Please enter verification code',
          rememberMe: 'Remember account',
          loginButton: 'Login',
          forgotPassword: 'Forgot Password?',
          secondsLater: 's later resend',
          loginProgress: 'Logging in...',
          registerTitle: 'Register Account',
          username: 'Username',
          password: 'Password',
          phone: 'Phone Number',
          clinicName: 'Clinic Name',
          clinicPlaceholder: 'Please enter clinic name',
          addButton: 'Add',
          resetButton: 'Reset',
          cancelButton: 'Cancel'
        }
      }
    };
  },
  computed: {
    language() {
      return this.languages[this.currentLanguage];
    }
  },
  mounted() {
    this.Time();
    this.getSystemInfo();
    this.loadSavedAccount();
    setTimeout(() => { this.isBlurActive = true }, 100);
    if (!window.crypto || !window.crypto.getRandomValues) {
      console.error('浏览器不支持Crypto API');
      this.$message.error('您的浏览器安全功能不完整，请升级浏览器');
    }
  },
  methods: {
    updateLoadingStatus(percentage, text, stepIndex = -1) {
      this.loadingPercentage = percentage;
      this.loadingText = text;
      if (stepIndex >= 0) {
        this.currentSteps.forEach((step, index) => {
          step.active = index === stepIndex;
          step.completed = index < stepIndex;
        });
      }
    },
    toggleTheme() {
      this.currentTheme = this.currentTheme === 'theme-light' ? 'theme-dark' : 'theme-light';
    },
    toggleLanguage() {
      this.currentLanguage = this.currentLanguage === 'zh' ? 'en' : 'zh';
    },
    handleDownload() {
      this.$message({ message: '开始下载客户端安装包...', type: 'success', duration: 2000 });
    },
    loadSavedAccount() {
      const savedAccount = localStorage.getItem('savedAccount');
      if (savedAccount) {
        try {
          const accountData = JSON.parse(savedAccount);
          this.loginForm.userName = accountData.userName;
          this.checked = true;
        } catch (e) {
          console.error('读取保存账户失败', e);
        }
      }
    },
    saveAccount() {
      if (this.checked && this.loginForm.userName) {
        const accountData = { userName: this.loginForm.userName, savedAt: new Date().toISOString() };
        localStorage.setItem('savedAccount', JSON.stringify(accountData));
      } else if (!this.checked) {
        localStorage.removeItem('savedAccount');
      }
    },
    clearSavedAccount() {
      localStorage.removeItem('savedAccount');
      this.checked = false;
      this.loginForm.userName = '';
      this.$message.success('已清除记住的账户');
    },
    getSystemInfo() {
      const systemInfo = {
        userAgent: navigator.userAgent,
        appName: navigator.appName,
        appVersion: navigator.appVersion,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: screen.width,
        screenHeight: screen.height,
        colorDepth: screen.colorDepth,
        ip: this.ip,
        Hostname: this.hostname,
        MachineID: this.machineId,
        informations: this.informations
      };
      this.systemInfo = systemInfo;
      localStorage.setItem("systemInfo", JSON.stringify(systemInfo));
    },
    handleClick(tab, event) {},
    Time() {
      setInterval(() => {
        this.seconds -= 1
        this.$router.go(0);
      }, 600000)
    },
    handleEnter() { this.submitForm(); },
    
    async submitForm() {
      this.loginForm.docDeviceInfoVO.machineId = this.machineId;
      this.loginForm.docDeviceInfoVO.localIp = this.ip;
      this.loginForm.docDeviceInfoVO.browser = navigator.vendor;
      this.loginForm.docDeviceInfoVO.os = navigator.userAgent.slice(13, 13 + 27);

      this.$refs.loginForm.validate(async (valid) => {
        if (valid) {
          this.isLoading = true;
          this.updateLoadingStatus(0, '开始登录流程...', 0);
          try {
            this.updateLoadingStatus(33, '验证账号密码中...', 0);
            const loginRes = await login(this.loginForm);
            if (loginRes.code !== 200) {
              this.updateLoadingStatus(100, `登录失败: ${loginRes.message || '未知错误'}`);
              this.$message.error(loginRes.message || '登录失败');
              setTimeout(() => { this.isLoading = false; }, 2000);
              return;
            }
            this.saveAccount();
            this.updateLoadingStatus(66, '账号验证成功，检查系统版本...', 1);
            
            this.token = loginRes.data.token;
            localStorage.setItem('token', this.token);
            localStorage.setItem('userInfo', JSON.stringify(loginRes.data.user));
            localStorage.setItem('terminalId', JSON.stringify(loginRes.data.terminalId));
            localStorage.setItem('permissions', JSON.stringify(loginRes.data.permissions));
            localStorage.setItem('loginTime', dayjs().format('YYYY-MM-DD HH:mm:ss'));

            if (loginRes.data.roles && loginRes.data.roles.length > 0) {
              localStorage.setItem('userRoles', JSON.stringify(loginRes.data.roles));
            }

            this.$store.commit('SET_LOGIN_DATA', {
              permissions: loginRes.data.permissions,
              user: loginRes.data.user,
              token: loginRes.data.token,
              roles: loginRes.data.roles || []
            });

            await this.$store.dispatch('preload/loadAll')
            await this.$store.dispatch('fetchDictData')

            this.updateLoadingStatus(75, '验证系统版本中...', 1);
            const versionValid = await this.verifyYbUser(loginRes.data.user);
            if (!versionValid) {
              this.updateLoadingStatus(100, '版本验证失败');
              this.$message.error('版本验证失败');
              setTimeout(() => { this.isLoading = false; }, 2000);
              return;
            }
            this.updateLoadingStatus(85, '版本验证成功，准备医保签到...', 2);

            if (loginRes.data.user.isInsurance === '1') {
              this.updateLoadingStatus(90, '执行医保签到中...', 2);
              const today = dayjs().format('YYYY-MM-DD');
              const lastSignDate = localStorage.getItem('signedDates');
              if (lastSignDate === today) {
                this.updateLoadingStatus(95, '医保已签到，完成登录...', 2);
                await new Promise(resolve => setTimeout(resolve, 1000));
              } else {
                if (lastSignDate && lastSignDate !== today) {
                  this.updateLoadingStatus(92, '执行医保签退...');
                  await this.handleYbSignOut(loginRes.data.user);
                  localStorage.removeItem('signedDates');
                  this.signNo = '';
                }
                this.updateLoadingStatus(95, '执行医保签到...');
                await this.handleYbSignIn(loginRes.data.user);
              }
            } else {
              this.updateLoadingStatus(95, '跳过医保签到...', 2);
            }

            this.updateLoadingStatus(100, '登录成功，正在跳转...');
            await this.setUserWatermark(loginRes.data.user);
            updateLastActiveTime();
            resetInactivityTimer();
            setTimeout(() => {
              this.isLoading = false;
              this.$router.push({ path: '/' });
              this.$message.success('登录成功');
            }, 1000);
          } catch (error) {
            console.error('登录过程中出错:', error);
            this.updateLoadingStatus(100, `登录失败: ${error.message}`);
            this.$message.error('登录过程中出错');
            setTimeout(() => { this.isLoading = false; }, 2000);
          }
        }
      });
    },
    async verifyYbUser(user) {
      try {
        const currentVersion = process.env.VUE_APP_VERSION || '1.0.0';
        await new Promise(resolve => setTimeout(resolve, 1000));
        return true;
      } catch (error) {
        return true;
      }
    },
    async handleYbSignIn(userData) {
      try {
        const today = dayjs().format('YYYY-MM-DD');
        const ybRequest = JSON.parse(JSON.stringify(ybRequestTemplate));
        this.machineId = this.machineId.slice(0, 20);
        ybRequest.body.infno = '9001';
        ybRequest.body.msgid = `${userData.clinicCode}${dayjs().format('YYYYMMDDHHmmss')}${this.getRandom4Digits()}`;
        ybRequest.body.mdtrtarea_admvs = userData.mdtrtareaAdmvs;
        ybRequest.body.recer_sys_code = '007';
        ybRequest.body.infver = 'V1.0';
        ybRequest.body.opter_type = '1';
        ybRequest.body.opter = userData.clinicCode;
        ybRequest.body.opter_name = userData.staffName;
        ybRequest.body.inf_time = dayjs().format('YYYY-MM-DD HH:mm:ss');
        ybRequest.body.fixmedins_code = userData.clinicCode;
        ybRequest.body.fixmedins_name = userData.clinicName;
        ybRequest.body.input.signIn = { opter_no: userData.staffId, mac: this.machineId, ip: this.ip };

        const newItem = { infno: 9001, mdtrtareaAdmvs: userData.mdtrtareaAdmvs, data: JSON.stringify(ybRequest.body), patientId: userData.staffId, patientName: userData.staffName };
        const res = await addYbRquestData(newItem);
        if (res.code !== 200) return true;

        this.lyToqdId = res.data.id;
        let responseData = res.data;
        if (typeof responseData === 'string') {
          try { responseData = JSON.parse(responseData); } catch (parseError) { return true; }
        }

        ybRequest.headers.infosyscode = responseData.infosyscode;
        ybRequest.headers.hsf_signature = responseData.signature;
        ybRequest.headers.fixmedins_code = userData.clinicCode;
        ybRequest.headers.hsf_nonce = responseData.hsfNonce;
        ybRequest.headers.hsf_timestamp = responseData.hsfTimestamp;

        const axiosConfig = { method: 'post', url: process.env.VUE_APP_BASE_YBHOST, baseURL: '', headers: ybRequest.headers, data: ybRequest.body };
        const response = await axios(axiosConfig);
        this.qdResult = response;

        await this.saveYbResponse(this.qdResult, 'signIn');

        if (this.qdResult.infcode === 0) {
          const signNoRes = await getqdNo();
          if (signNoRes.code === 200) {
            this.signNo = signNoRes.data.signNo;
            localStorage.setItem('signedDates', today);
            return true;
          }
          return true;
        } else {
          this.$message.warning(`医保签到失败: ${this.qdResult.err_msg || '未知错误'} 请联系管理或销售人员！`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          return true;
        }
      } catch (error) {
        this.$message.warning('医保签到过程出现异常，但不影响系统登录，请联系管理或销售人员！');
        await new Promise(resolve => setTimeout(resolve, 1000));
        return true;
      }
    },
    async handleYbSignOut(userData) {
      try {
        const signNoRes = await getqdNo();
        if (signNoRes.code != 200 || !signNoRes.data.expire) return true;
        
        this.signNo = signNoRes.data.signNo;
        const ybRequest = JSON.parse(JSON.stringify(ybRequestTemplate));
        this.machineId = this.machineId.slice(0, 20);
        ybRequest.body.infno = '9002';
        ybRequest.body.msgid = `${userData.clinicCode}${dayjs().format('YYYYMMDDHHmmss')}${this.getRandom4Digits()}`;
        ybRequest.body.mdtrtarea_admvs = userData.mdtrtareaAdmvs;
        ybRequest.body.recer_sys_code = '007';
        ybRequest.body.infver = 'V1.0';
        ybRequest.body.opter_type = '1';
        ybRequest.body.opter = userData.clinicCode;
        ybRequest.body.opter_name = userData.staffName;
        ybRequest.body.inf_time = dayjs().format('YYYY-MM-DD HH:mm:ss');
        ybRequest.body.fixmedins_code = userData.clinicCode;
        ybRequest.body.fixmedins_name = userData.clinicName;
        ybRequest.body.sign_no = this.signNo;
        ybRequest.body.input.signOut = { sign_no: this.signNo, opter_no: userData.staffId };

        const newItem = { infno: 9002, mdtrtareaAdmvs: userData.mdtrtareaAdmvs, data: JSON.stringify(ybRequest.body), patientId: userData.staffId, patientName: userData.staffName };
        const res = await addYbRquestData(newItem);
        if (res.code !== 200) return true;

        this.lyToqtId = res.data.id;
        let responseData = res.data;
        if (typeof responseData === 'string') {
          try { responseData = JSON.parse(responseData); } catch (parseError) { return true; }
        }

        ybRequest.headers.infosyscode = responseData.infosyscode;
        ybRequest.headers.hsf_signature = responseData.signature;
        ybRequest.headers.fixmedins_code = userData.clinicCode;
        ybRequest.headers.hsf_nonce = responseData.hsfNonce;
        ybRequest.headers.hsf_timestamp = responseData.hsfTimestamp;

        const axiosConfig = { method: 'post', url: process.env.VUE_APP_BASE_YBHOST, baseURL: '', headers: ybRequest.headers, data: ybRequest.body };
        const response = await axios(axiosConfig);
        this.qtResult = response;

        if (this.qtResult.infcode === 0) {
          await this.saveYbResponse(this.qtResult, 'signOut');
          this.signNo = '';
          return true;
        } else {
          this.$message.warning(`医保签退失败: ${this.qtResult.err_msg || '未知错误'} 但不影响系统登录`);
          return true;
        }
      } catch (error) {
        return true;
      }
    },
    async saveYbResponse(response, type) {
      try {
        const infno = type === 'signIn' ? '9001' : '9002';
        const id = type === 'signIn' ? this.lyToqdId : this.lyToqtId;
        if (!id) return false;
        const params = { data: JSON.stringify(response), id: id, infno: infno, type: type, timestamp: dayjs().format('YYYY-MM-DD HH:mm:ss') };
        const res = await toUploadLY(params);
        return res.code === 200;
      } catch (error) {
        return false;
      }
    },
    generateSignature(timestamp, secret, nonce) {
      return sha256(`${timestamp}${secret}${nonce}`);
    },
    generateBrowserNonce(length = 32) {
      const array = new Uint8Array(length);
      window.crypto.getRandomValues(array);
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    },
    getRandom4Digits() {
      return Math.floor(1000 + Math.random() * 9000).toString();
    },
    async setUserWatermark(user) {
      try {
        const clinicList = await doccliniclist();
        const clinic = clinicList.data.find(item => item.id === user.clinicId);
        const clinicName = clinic ? clinic.name : '未知诊所';
        const currentDate = dayjs().format('YYYY-MM-DD');
        const watermarkText = `${clinicName}-${user.staffName}-${currentDate}`;
        localStorage.setItem('watermarkText', watermarkText);
        sessionStorage.setItem('w_part1', clinicName);
        localStorage.setItem('w_part2', user.staffName);
        sessionStorage.setItem('w_part3', currentDate);
        localStorage.setItem('w_hash', this.generateHash(watermarkText));
        watermark.set(watermarkText);
      } catch (error) {
        watermark.set('九阵软件');
      }
    },
    generateHash(text) {
      let hash = 0;
      for (let i = 0; i < text.length; i++) {
        hash = ((hash << 5) - hash) + text.charCodeAt(i);
        hash |= 0;
      }
      return hash.toString();
    }
  }
};
</script>

<style lang="less" scoped>
/* 主题变量定义 */
:root {
  --bg-color: #f5f7fa;
  --text-color: #333;
  --form-bg: #fff;
  --input-bg: #fff;
  --input-border: #dcdfe6;
  --primary-color: #409eff;
  --secondary-text: #909399;
  --curve-color: #409eff;
}

.theme-light {
  --bg-color: #f5f7fa;
  --text-color: #333;
  --form-bg: #fff;
  --input-bg: #fff;
  --input-border: #dcdfe6;
  --primary-color: #409eff;
  --secondary-text: #909399;
  --curve-color: #409eff;
}

.theme-dark {
  --bg-color: #1a1a1a;
  --text-color: #e6e6e6;
  --form-bg: #2d2d2d;
  --input-bg: #3d3d3d;
  --input-border: #555;
  --primary-color: #409eff;
  --secondary-text: #a0a0a0;
  --curve-color: #409eff;
}

/* 核心修复：取消高度和 overflow 限制，允许全局纵向滚动 */
#app {
  min-height: 100vh;
  background-color: var(--bg-color);
  color: var(--text-color);
  transition: all 0.3s ease;
  overflow-y: auto;
  overflow-x: hidden;
}

/* 登录容器样式：大屏时全屏铺满，小屏时自适应撑开 */
.login-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  transition: all 0.5s ease;

  &.hidden {
    opacity: 0;
    visibility: hidden;
  }
}

/* 大屏端保持原有的左右布局 */
@media (min-width: 769px) {
  .login-container {
    flex-direction: row;
    height: 100vh;
    overflow: hidden;
  }
  .left-panel {
    flex: 1.5;
  }
  .right-panel {
    flex: 1;
  }
}

/* 全屏加载动画样式 */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #fff;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  transition: opacity 0.5s ease;
}

.loading-container {
  text-align: center;
  max-width: 500px;
  width: 90%;
}

/* 动态图标动画 */
.logo-animation {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 40px;
  .logo-icon {
    display: inline-block;
    position: relative;
    width: 100px;
    height: 100px;
    .logo-part {
      position: absolute;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      transition: all 0.5s ease;
      animation-duration: 2s;
      animation-iteration-count: infinite;
      animation-timing-function: ease-in-out;
    }
    .part1 { background: #409EFF; top: 0; left: 0; animation-name: logoPart1; }
    .part2 { background: #67C23A; top: 0; right: 0; animation-name: logoPart2; }
    .part3 { background: #E6A23C; bottom: 0; left: 50%; transform: translateX(-50%); animation-name: logoPart3; }
  }
}
@keyframes logoPart1 { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(-20px, -20px); } }
@keyframes logoPart2 { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(20px, -20px); } }
@keyframes logoPart3 { 0%, 100% { transform: translate(-50%, 0); } 50% { transform: translate(-50%, 20px); } }

/* 进度条样式 */
.progress-container {
  margin-bottom: 30px;
  .progress-bar {
    width: 100%;
    height: 8px;
    background: #e4e7ed;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 10px;
  }
  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #409EFF, #67C23A);
    border-radius: 4px;
    transition: width 0.5s ease;
  }
  .progress-text { font-size: 18px; font-weight: bold; color: #409EFF; }
}

/* 加载内容样式 */
.loading-content {
  text-align: center;
  .loading-text { font-size: 20px; font-weight: 600; margin-bottom: 20px; color: #409EFF; }
  .loading-steps {
    .step-item {
      display: flex;
      align-items: center;
      margin: 12px 0;
      font-size: 16px;
      color: #606266;
      &.active { color: #409EFF; font-weight: 600; }
      &.completed { color: #67C23A; }
      .step-dot { width: 10px; height: 10px; border-radius: 50%; background: #c0c4cc; margin-right: 12px; }
      i { margin-right: 12px; font-size: 18px; }
      .step-text { flex: 1; text-align: left; }
    }
  }
}

/* ====== 左面板 ====== */
.left-panel {
  position: relative;
  background: url("@/assets/images/loginbg.png") center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(135deg, rgba(30, 60, 114, 0.85) 0%, rgba(42, 82, 152, 0.85) 100%);
    z-index: 1;
  }
}

/* 曲线分割 */
.curve-divider {
  position: absolute;
  right: 0; top: 0; bottom: 0;
  width: 60px;
  z-index: 10;
  color: var(--form-bg);
  svg { width: 100%; height: 100%; }
}

/* 左侧内容区 */
.left-content {
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 500px;
  padding: 0 20px;
  animation: fadeInUp 0.8s ease;
}

.desktop-illustration {
  width: 280px;
  height: 180px;
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  margin: 0 auto 30px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.desktop-screen {
  width: 85%;
  height: 65%;
  background: linear-gradient(145deg, #f0f2f5, #e1e5eb);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.screen-item {
  height: 100%;
  background: url("@/assets/images/logoBg.png") center/cover no-repeat;
  margin-bottom: 8px;
  border-radius: 2px;
}
.screen-item:nth-child(1) { width: 65%; }
.screen-item:nth-child(2) { width: 75%; }
.screen-item:nth-child(3) { width: 85%; }

.plant, .pen-holder {
  position: absolute;
  bottom: -12px;
  width: 24px; height: 24px;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}
.plant { left: 40px; background: linear-gradient(145deg, #4caf50, #388e3c); }
.pen-holder { right: 40px; background: linear-gradient(145deg, #2196f3, #1976d2); }

.slogan {
  font-size: 28px;
  margin-bottom: 15px;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.sub-slogan {
  font-size: 16px;
  opacity: 0.9;
  line-height: 1.5;
}

/* 下载按钮 */
.download-section {
  margin-top: 30px;
  text-align: center;
  position: relative;
  z-index: 2;
}

.download-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 28px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 40px;
  color: #fff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  i { font-size: 18px; transition: transform 0.3s ease; }
  .download-text { letter-spacing: 1px; }
  .download-hint {
    font-size: 12px;
    opacity: 0.8;
    background: rgba(255, 255, 255, 0.2);
    padding: 2px 8px;
    border-radius: 20px;
    margin-left: 4px;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    i { transform: translateY(2px); animation: bounce 0.5s ease; }
  }
  &:active { transform: translateY(0); }
}
@keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(4px); } }

/* ====== 右面板 ====== */
.right-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background-color: var(--form-bg);
  position: relative;
}

.login-form {
  width: 100%;
  max-width: 380px;
  padding: 30px;
  background: var(--form-bg);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  animation: fadeInUp 0.6s ease;
}

.title {
  margin-bottom: 30px;
  text-align: center;
}

.login-cont {
  background-color: var(--form-bg);
  padding: 20px;
  border-radius: 12px;
}

.chose {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.clear-account {
  font-size: 12px;
  color: #909399;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.3s;
  i { font-size: 12px; }
  &:hover { color: #f56c6c; }
}

/* 右上角按钮 */
.theme-toggle {
  position: absolute;
  top: 20px; right: 80px;
  background: none; border: none; cursor: pointer;
  font-size: 24px; color: var(--text-color); z-index: 10;
  transition: transform 0.3s;
  &:hover { transform: scale(1.1); }
}

.lang-toggle {
  position: absolute;
  top: 20px; right: 30px;
  background: none; border: none; cursor: pointer;
  font-size: 16px; color: var(--text-color); z-index: 10;
  padding: 6px 12px; border-radius: 6px;
  background: rgba(64, 158, 255, 0.1);
  transition: background 0.3s;
  &:hover { background: rgba(64, 158, 255, 0.2); }
}

/* =========================================================
   移动端 & 小屏幕适配核心代码
   ========================================================= */
@media (max-width: 768px) {
  /* 左侧面板：改为上下排布时占据较小空间 */
  .left-panel {
    flex: none;
    padding: 50px 20px 30px;
    min-height: auto;
  }
  
  /* 隐藏占空间的装饰元素 */
  .desktop-illustration {
    display: none; 
  }
  .curve-divider {
    display: none;
  }
  
  .slogan {
    font-size: 22px;
    margin-top: 10px;
  }
  .sub-slogan {
    font-size: 14px;
  }
  
  .download-section {
    margin-top: 20px;
  }
  .download-link {
    padding: 10px 20px;
    .download-text { font-size: 13px; }
    .download-hint { font-size: 10px; }
  }

  /* 右侧表单区：去掉多余的外边距，适应手机屏幕 */
  .right-panel {
    flex: none;
    padding: 20px;
  }
  
  .login-form {
    box-shadow: none; /* 去掉笨重的阴影 */
    padding: 0; /* 去掉多余的 padding */
    max-width: 100%;
    border-radius: 0;
  }
  
  .login-cont {
    padding: 0;
  }

  .theme-toggle, .lang-toggle {
    position: fixed;
    top: 15px;
  }
  .theme-toggle { right: 70px; }
  .lang-toggle { right: 20px; }
}

/* 极窄屏幕适配 */
@media (max-width: 480px) {
  .slogan { font-size: 20px; }
  .sub-slogan { font-size: 13px; }
  .login-form { padding: 10px 0; }
  .title { margin-bottom: 20px; }
}

/* Element UI 组件样式适配 */
.el-input {
  /deep/ .el-input__inner {
    background-color: var(--input-bg);
    color: var(--text-color);
    border-color: var(--input-border);
    border-radius: 8px;
    height: 44px;
    padding: 0 28px;
    transition: border-color 0.3s;
    &:focus {
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
    }
  }
}

.el-tabs {
  /deep/ .el-tabs__item { color: var(--text-color); font-weight: 500; }
  /deep/ .el-tabs__active-bar { background-color: var(--primary-color); }
}

.el-button--primary {
  background: linear-gradient(135deg, var(--primary-color), #66b1ff);
  border: none;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s;
  &:hover {
    background: linear-gradient(135deg, #66b1ff, var(--primary-color));
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
  }
}

.el-checkbox {
  /deep/ .el-checkbox__label { color: var(--text-color); }
}

/* 动画效果 */
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>